import { useState, useEffect } from "react";
import {
  X,
  FileText,
  Package,
  Loader2,
  ChevronRight,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Building2,
  Calendar,
  DollarSign,
} from "lucide-react";
import API_BASE_URL from "../config";

/* ─────────────────────────────────
   CONSTANTS
   ───────────────────────────────── */
const STATUS = {
  0: { label: "Draft", bg: "bg-gray-100", text: "text-gray-600", Icon: AlertCircle },
  1: { label: "Pending", bg: "bg-yellow-100", text: "text-yellow-700", Icon: Clock },
  2: { label: "Approved", bg: "bg-green-100", text: "text-green-700", Icon: CheckCircle2 },
  3: { label: "Rejected", bg: "bg-red-100", text: "text-red-700", Icon: XCircle },
};

const fmtDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d) ? "—" : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/* ─────────────────────────────────
   MAIN — Pr-Po-TagsItem
   ───────────────────────────────── */
export default function PrPoTagsItem({
  request,
  prDetails = [],
  companies = {},
  expenseTypes = {},
  locations = {},
  departments = {},
  onClose,
}) {
  /* ── state ── */
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [linkedPOs, setLinkedPOs] = useState([]);
  const [poDetailMap, setPoDetailMap] = useState({});   // prDetailId → [{ poNo, poHeaderId, qty }]
  const [grMap, setGrMap] = useState({});   // poHeaderId  → [{ grNo }]

  /* ── slide-in on mount ── */
  useEffect(() => {
    const id = setTimeout(() => setOpen(true), 30);
    return () => clearTimeout(id);
  }, []);

  /* ── close with slide-out ── */
  const handleClose = () => {
    setOpen(false);
    setTimeout(() => onClose?.(), 280);
  };

  /* ── fetch PO + GR ── */
  useEffect(() => {
    if (!request?.id) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        /* 1 — PO Headers na naka-ref sa PR */
        const poHeaderRes = await fetch(`${API_BASE_URL}api/T_PO_Header`);
        const allPOHeaders = await poHeaderRes.json();
        const matched = (allPOHeaders || []).filter((h) => h.referencePRId === request.id);
        setLinkedPOs(matched);

        if (!matched.length) {
          setPoDetailMap({});
          setGrMap({});
          setLoading(false);
          return;
        }

        /* 2 — PO Details per PO → group sa referencePRDetailsId */
        const dMap = {};
        const poIds = [];

        for (const po of matched) {
          poIds.push(po.id);
          try {
            const res = await fetch(`${API_BASE_URL}api/T_Po_Details?poHeaderId=${po.id}`);
            const details = await res.json();
            (details || []).forEach((d) => {
              const key = d.referencePRDetailsId;
              if (!key) return;
              if (!dMap[key]) dMap[key] = [];
              dMap[key].push({ poNo: po.poNumber, poHeaderId: po.id, qty: d.qty });
            });
          } catch (e) { console.warn("T_Po_Details err", po.id, e); }
        }
        setPoDetailMap(dMap);

        /* 3 — GR Headers → match sa PO */
        /* 3 — GR Headers → match sa PO */
        const grRes = await fetch(`${API_BASE_URL}api/GR_Header`);
        const allGRs = await grRes.json();

        const poNoToId = {};
        matched.forEach((p) => { poNoToId[p.poNumber] = p.id; });

        // Deduplicate GRs using a Set per poId
        const gMapSets = {}; // poId → Set of grNo strings
        (allGRs || []).forEach((gr) => {
          const poId = gr.poId || poNoToId[gr.poNumber];
          if (!poId || !poIds.includes(poId)) return;
          if (!gMapSets[poId]) gMapSets[poId] = new Set();
          if (gr.grNo) gMapSets[poId].add(gr.grNo.trim());
        });

        // Convert Sets → array of objects
        const gMap = {};
        Object.entries(gMapSets).forEach(([poId, grSet]) => {
          gMap[poId] = [...grSet].map(grNo => ({ grNo }));
        });
        setGrMap(gMap);

      } catch (e) { console.error("PrPoTagsItem fetch", e); }
      finally { setLoading(false); }
    };

    fetchData();
  }, [request?.id]);

  /* ── helpers ── */
  const statusInfo = STATUS[request?.status] ?? STATUS[0];
  const StatusIcon = statusInfo.Icon;

  const getAllocBadge = (item) => {
    const alloc = item.userQty || 0;
    if (alloc <= 0) return { label: "Available", cls: "bg-emerald-100 text-emerald-700" };
    if (alloc >= (item.qty || 0)) return { label: "Fully Allocated", cls: "bg-red-100 text-red-700" };
    return { label: "Partial", cls: "bg-amber-100 text-amber-700" };
  };

  const totalCost = prDetails.reduce((s, d) => s + (d.costEstimate || 0), 0);

  /* ── render ── */
  return (
    <>
      {/* backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black transition-opacity duration-300 ${open ? "opacity-40" : "opacity-0"}`}
        onClick={handleClose}
      />

      {/* sidebar */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-[480px] max-w-[92vw] bg-white shadow-2xl border-l border-gray-200 flex flex-col
                    transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* ═══ HEADER ═══ */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white flex-shrink-0">
          <div className="flex items-center justify-between px-5 pt-5 pb-2">
            <div className="flex items-center gap-2.5">
              <FileText size={19} />
              <span className="text-lg font-extrabold tracking-tight">{request?.prNo}</span>
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${statusInfo.bg} ${statusInfo.text}`}>
                <StatusIcon size={12} />
                {statusInfo.label}
              </span>
            </div>
            <button onClick={handleClose} className="p-1 rounded-lg hover:bg-white/20 transition">
              <X size={20} />
            </button>
          </div>
          {/* meta pills */}
          <div className="px-5 pb-3 flex flex-wrap gap-1.5">
            <Pill icon={Building2}>{companies[request?.companyId] || "—"}</Pill>
            <Pill icon={FileText}>{expenseTypes[request?.expenseTypeId] || "—"}</Pill>
            <Pill icon={Calendar}>Need: {fmtDate(request?.dateNeeded)}</Pill>
          </div>
        </div>

        {/* ═══ BODY (scrolls) ═══ */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">

          {/* linked PO summary */}
          <LinkedPORow linkedPOs={linkedPOs} grMap={grMap} loading={loading} />

          <div className="border-t border-gray-200" />

          {/* label */}
          <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">
            Items <span className="text-gray-300">({prDetails.length})</span>
          </div>

          {/* spinner */}
          {loading && (
            <div className="flex items-center justify-center py-14">
              <Loader2 size={24} className="animate-spin text-blue-500" />
            </div>
          )}

          {/* item cards */}
          {!loading &&
            prDetails.map((item, idx) => (
              <ItemCard
                key={item.id}
                idx={idx}
                item={item}
                pos={poDetailMap[item.id] || []}
                grMap={grMap}
                badge={getAllocBadge(item)}
              />
            ))}

          {/* empty state */}
          {!loading && prDetails.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-300">
              <Package size={36} className="mb-2" />
              <span className="text-sm">Walang items.</span>
            </div>
          )}
        </div>

        {/* ═══ FOOTER ═══ */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-5 py-3 flex justify-end">
          <button onClick={handleClose} className="text-sm font-semibold text-gray-500 hover:text-red-500 transition">
            Close
          </button>
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────
   SUB-COMPONENTS
   ───────────────────────────────── */

function Pill({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-white/15 rounded-lg px-2.5 py-1 text-xs">
      <Icon size={13} />
      {children}
    </span>
  );
}

function LinkedPORow({ linkedPOs, grMap, loading }) {
  return (
    <div>
      <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1.5">Linked POs</div>
      {loading ? (
        <span className="text-xs text-gray-400 italic">Loading…</span>
      ) : linkedPOs.length === 0 ? (
        <span className="text-xs text-gray-400 italic">Walang PO pa.</span>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {linkedPOs.map((po) => {
            const grs = grMap[po.id] || [];
            return (
              <div key={po.id} className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-1.5">
                <FileText size={13} className="text-blue-600" />
                <span className="text-xs font-bold text-blue-700">{po.poNumber}</span>
                {grs.length === 0 ? (
                  <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">No GR</span>
                ) : (
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">
                    GR: {grs.map((g) => g.grNo).join(", ")}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ItemCard({ idx, item, pos, grMap, badge }) {
  const [expanded, setExpanded] = useState(false);
  const ordered = item.qty || 0;
  const allocated = item.userQty || 0;
  const remaining = ordered - allocated;
  const pct = ordered > 0 ? Math.min((allocated / ordered) * 100, 100) : 0;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-start justify-between gap-2 px-4 pt-3 pb-1">
        <div className="flex items-start gap-2.5 min-w-0">
          <div className="flex-shrink-0 w-6 h-6 mt-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">
            {idx + 1}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-800 truncate">{item.itemDescription}</p>
            <p className="text-xs text-gray-400">{item.purpose || "—"} &bull; {item.uom}</p>
          </div>
        </div>
        <span className={`flex-shrink-0 text-xs font-bold px-2.5 py-0.5 rounded-full ${badge.cls}`}>
          {badge.label}
        </span>
      </div>

      <div className="flex items-center justify-center gap-3 px-4 pt-2 pb-1">
        <QtyBox label="Ordered" value={ordered} color="text-gray-700" />
        <ChevronRight size={14} className="text-gray-300" />
        <QtyBox label="Allocated" value={allocated} color={allocated > 0 ? "text-orange-600" : "text-gray-300"} labelColor="text-orange-500" />
        <ChevronRight size={14} className="text-gray-300" />
        <QtyBox label="Remaining" value={remaining} color={remaining <= 0 ? "text-red-600" : "text-emerald-600"} labelColor="text-emerald-500" />
      </div>

      <div className="px-4 pt-1 pb-2.5">
        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-red-400" : pct > 0 ? "bg-orange-400" : "bg-emerald-400"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <div className="px-4 pb-3">
        {pos.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Walang PO pa para dito.</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {pos.map((po, i) => {
              const grs = grMap[po.poHeaderId] || [];
              return (
                <div key={i} className="flex flex-col gap-1">
                  {/* Clickable PO pill */}
                  <button
                    onClick={() => setExpanded(v => !v)}
                    className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-1 w-fit hover:bg-blue-100 transition cursor-pointer"
                  >
                    <FileText size={13} className="text-blue-600" />
                    <span className="text-xs font-bold text-blue-700">{po.poNo}</span>
                    <span className="text-xs text-blue-400">({po.qty} {item.uom})</span>
                    <ChevronRight
                      size={12}
                      className={`text-blue-400 transition-transform ${expanded ? "rotate-90" : ""}`}
                    />
                  </button>

                  {/* GR pills — only show when expanded */}
                  {expanded && (
                    <div className="ml-5 flex flex-wrap gap-1">
                      {grs.length === 0 ? (
                        <span className="text-xs text-gray-400 italic">No GR yet</span>
                      ) : (
                        grs.map((gr, gi) => (
                          <span key={gi} className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-0.5">
                            <Package size={11} className="text-emerald-600" />
                            <span className="text-xs font-bold text-emerald-700">{gr.grNo}</span>
                          </span>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function QtyBox({ label, value, color = "text-gray-700", labelColor = "text-gray-400" }) {
  return (
    <div className="flex flex-col items-center flex-1">
      <span className={`text-xs ${labelColor} mb-0.5`}>{label}</span>
      <span className={`text-base font-bold ${color}`}>{value}</span>
    </div>
  );
}